import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ZoomIn, RotateCcw, Check, X, Crop, Maximize2 } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { geom } from "@/lib/cropGeometry";

const OUT_W = 800;      // what the illustrator actually receives
const MAX_ZOOM = 4;

const SHAPES = [
    { key: "square", label: "Square", ratio: 1 },
    { key: "portrait", label: "Portrait", ratio: 3 / 4 },
];

/**
 * Crop for a character reference photo: drag to move, pinch or slide to zoom.
 *
 * Rendered through a portal: framer-motion leaves a `transform` on the wizard
 * step that wraps the upload, and a transformed ancestor becomes the containing
 * block for `position: fixed` — which pinned this overlay inside the step card
 * instead of over the page.
 *
 * The frame is sized in viewport and absolute units only, never a percentage of
 * the scrolling backdrop. A percentage there sets up a feedback loop — scrollbar
 * appears, container narrows, frame shrinks, card shortens, scrollbar goes, and
 * round again — which reads on screen as the whole page vibrating.
 *
 * Written rather than pulled in: react-easy-crop conflicts with this project's
 * peer range. Geometry lives in one place — `fill` takes the larger scale so the
 * frame is covered, `fit` takes the smaller so the whole photo survives, zoom
 * multiplies whichever, and the offset is clamped to whatever room is left.
 */
export default function PhotoCropper({ src, onCancel, onDone }) {
    const boxRef = useRef(null);
    const imgRef = useRef(null);
    const [nat, setNat] = useState(null);          // natural size, once loaded
    const [shape, setShape] = useState("square");
    const [fit, setFit] = useState(false);         // false = crop to fill, true = whole photo
    const [zoom, setZoom] = useState(1);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const drag = useRef(null);
    const pinch = useRef(null);

    const ratio = (SHAPES.find((s) => s.key === shape) || SHAPES[0]).ratio;
    const frame = () => {
        const r = boxRef.current?.getBoundingClientRect();
        return { w: r?.width || 1, h: r?.height || 1 };
    };

    const clamp = useCallback((o, z) => {
        const g = geom(nat, frame(), fit, z, o);
        return g ? g.offset : o;
    }, [nat, fit]);

    // Changing shape or mode can leave the photo parked outside the new frame.
    useEffect(() => { setOffset((o) => clamp(o, zoom)); }, [zoom, shape, fit, clamp]);

    // Zero room means it already sits exactly in the frame, so the hint has to
    // say that rather than let the drag feel dead.
    const live = geom(nat, nat ? frame() : { w: 1, h: 1 }, fit, zoom, offset);
    const eff = live ? live.scale : 1;
    const canPan = !!live && (live.room.x > 1 || live.room.y > 1);

    const onPointerDown = (e) => {
        if (e.pointerType === "touch" && e.isPrimary === false) return;
        e.currentTarget.setPointerCapture?.(e.pointerId);
        drag.current = { x: e.clientX, y: e.clientY, from: offset };
    };
    const onPointerMove = (e) => {
        if (!drag.current) return;
        const d = drag.current;
        setOffset(clamp({ x: d.from.x + (e.clientX - d.x), y: d.from.y + (e.clientY - d.y) }, zoom));
    };
    const endDrag = () => { drag.current = null; };

    // Two fingers: distance drives zoom, the same way every photo app does it.
    const onTouchStart = (e) => {
        if (e.touches.length !== 2) return;
        drag.current = null;
        const [a, b] = e.touches;
        pinch.current = { d: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY), z: zoom };
    };
    const onTouchMove = (e) => {
        if (e.touches.length !== 2 || !pinch.current) return;
        e.preventDefault();
        const [a, b] = e.touches;
        const d = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
        setZoom(Math.min(MAX_ZOOM, Math.max(1, pinch.current.z * (d / pinch.current.d))));
    };
    const onTouchEnd = () => { pinch.current = null; };

    const onWheel = (e) => {
        e.preventDefault();
        setZoom((z) => Math.min(MAX_ZOOM, Math.max(1, z - e.deltaY * 0.0015)));
    };

    const reset = () => { setZoom(1); setOffset({ x: 0, y: 0 }); };

    const apply = () => {
        const img = imgRef.current;
        if (!img || !nat) return;
        const fr = frame();
        const g = geom(nat, fr, fit, zoom, offset);
        if (!g) return;
        const outH = Math.round(OUT_W / ratio);
        const k = OUT_W / fr.w;                       // frame pixels -> output pixels
        const { dx, dy, dw, dh } = g;

        const canvas = document.createElement("canvas");
        canvas.width = OUT_W;
        canvas.height = outH;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#FFFBF5";                    // shows through in fit mode
        ctx.fillRect(0, 0, OUT_W, outH);
        // Same placement as the preview, so what they framed is what they get.
        ctx.drawImage(img, dx * k, dy * k, dw * k, dh * k);
        onDone(canvas.toDataURL("image/jpeg", 0.9));
    };

    // Absolute and viewport units only — see the note above about vibration.
    const frameMaxWidth = `min(calc(100vw - 3rem), 25.5rem, calc(50vh * ${ratio}))`;

    return createPortal(
        <div className="fixed inset-0 z-50 bg-plum/70 backdrop-blur-sm flex items-center justify-center
                        overflow-y-auto p-4"
             style={{ scrollbarGutter: "stable" }}
             data-testid="photo-cropper" onClick={onCancel}>
            <div className="card-cloud w-full max-w-md p-5 my-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <div className="font-display text-2xl text-plum">Frame the face</div>
                        <div className="script-note text-xl">
                            {fit
                                ? "the whole photo, nothing cut"
                                : canPan ? "drag to move, pinch or slide to zoom"
                                         : "zoom in to move the photo around"}
                        </div>
                    </div>
                    <button onClick={onCancel} aria-label="Cancel"
                            className="text-plum/50 hover:text-plum shrink-0" data-testid="crop-cancel">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Shape, and an escape hatch when a crop would cut the head off. */}
                <div className="flex flex-wrap items-center gap-2 mt-4">
                    {SHAPES.map((s) => (
                        <button key={s.key} onClick={() => setShape(s.key)}
                                data-testid={`crop-shape-${s.key}`}
                                className={`rounded-full px-3 h-8 text-xs font-medium border transition-colors ${
                                    shape === s.key
                                        ? "bg-plum text-paper border-plum"
                                        : "border-plum/15 text-plum/70 hover:bg-plum/5"}`}>
                            {s.label}
                        </button>
                    ))}
                    <button onClick={() => { setFit((v) => !v); setZoom(1); setOffset({ x: 0, y: 0 }); }}
                            data-testid="crop-fit-toggle"
                            title="Keep the whole photo instead of cropping into it"
                            className={`rounded-full px-3 h-8 text-xs font-medium border transition-colors
                                        inline-flex items-center gap-1.5 ml-auto ${
                                fit ? "bg-mint text-plum border-mint"
                                    : "border-plum/15 text-plum/70 hover:bg-plum/5"}`}>
                        {fit ? <Maximize2 className="w-3.5 h-3.5" /> : <Crop className="w-3.5 h-3.5" />}
                        {fit ? "Whole photo" : "Fit whole photo"}
                    </button>
                </div>

                <div
                    ref={boxRef}
                    className={`relative mt-3 w-full mx-auto rounded-2xl overflow-hidden bg-plum/10 touch-none ${
                        canPan ? "cursor-grab active:cursor-grabbing" : "cursor-default"}`}
                    style={{ aspectRatio: String(ratio), maxWidth: frameMaxWidth }}
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={endDrag}
                    onPointerCancel={endDrag}
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                    onWheel={onWheel}
                >
                    <img
                        ref={imgRef}
                        src={src}
                        alt=""
                        draggable={false}
                        onLoad={(e) => setNat({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
                        className="absolute left-1/2 top-1/2 max-w-none select-none"
                        style={nat ? {
                            width: nat.w * eff,
                            height: nat.h * eff,
                            transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px))`,
                        } : { opacity: 0 }}
                    />
                    {/* Head-room guide rather than a face circle: a likeness needs
                        the hair, and a centred circle invited cropping it off. */}
                    <div className="pointer-events-none absolute inset-0">
                        <div className="absolute inset-x-[10%] top-[6%] bottom-[10%] rounded-[45%] border-2 border-white/60" />
                        <div className="absolute inset-0 border-[3px] border-white/25 rounded-2xl" />
                    </div>
                </div>

                <div className="flex items-center gap-3 mt-4">
                    <ZoomIn className="w-4 h-4 text-plum/50 shrink-0" />
                    <Slider min={1} max={MAX_ZOOM} step={0.01} value={[zoom]}
                            onValueChange={([v]) => setZoom(v)} className="flex-1"
                            data-testid="crop-zoom" aria-label="Zoom" />
                    <button onClick={reset} title="Reset"
                            className="text-plum/50 hover:text-plum shrink-0" data-testid="crop-reset">
                        <RotateCcw className="w-4 h-4" />
                    </button>
                </div>

                <div className="flex gap-3 mt-5">
                    <button onClick={onCancel}
                            className="flex-1 rounded-full h-11 text-sm font-medium border border-plum/15 text-plum hover:bg-plum/5">
                        Pick another
                    </button>
                    <button onClick={apply} disabled={!nat}
                            className="btn-sticker flex-1 disabled:opacity-60" data-testid="crop-apply">
                        <Check className="w-4 h-4 mr-1 inline" /> Use this photo
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
}
